/**
 * File upload hook with explicit state machine.
 * States: idle → uploading → success/error
 */
import { useState, useCallback } from 'react';
const DEFAULT_MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
export function useFileUpload(options) {
    const { uploadService, category, allowedMimeTypes, maxFileSize = DEFAULT_MAX_FILE_SIZE, onSuccess, onError } = options;
    const [state, setState] = useState({ status: 'idle' });
    const upload = useCallback(async (file) => {
        // Client-side validation
        if (allowedMimeTypes && !allowedMimeTypes.includes(file.type)) {
            const error = {
                code: 'INVALID_MIME_TYPE',
                message: `File type ${file.type} is not allowed. Allowed: ${allowedMimeTypes.join(', ')}`,
            };
            setState({ status: 'error', error });
            onError?.(error);
            return;
        }
        if (file.size > maxFileSize) {
            const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
            const error = {
                code: 'FILE_TOO_LARGE',
                message: `File size exceeds ${maxSizeMB}MB limit`,
            };
            setState({ status: 'error', error });
            onError?.(error);
            return;
        }
        // Start upload
        setState({ status: 'uploading', file });
        try {
            const response = await uploadService.upload(file, { category });
            if (response.success) {
                setState({ status: 'success', result: response.data });
                onSuccess?.(response.data);
            }
            else {
                // Type narrowing: response.success is false, so error exists
                const errorResponse = response;
                setState({ status: 'error', error: errorResponse.error });
                onError?.(errorResponse.error);
            }
        }
        catch (err) {
            const error = {
                code: 'UPLOAD_FAILED',
                message: err instanceof Error ? err.message : 'Upload failed',
            };
            setState({ status: 'error', error });
            onError?.(error);
        }
    }, [uploadService, category, allowedMimeTypes, maxFileSize, onSuccess, onError]);
    const reset = useCallback(() => {
        setState({ status: 'idle' });
    }, []);
    // Computed helpers
    const isUploading = state.status === 'uploading';
    const isSuccess = state.status === 'success';
    const isError = state.status === 'error';
    const result = state.status === 'success' ? state.result : null;
    const error = state.status === 'error' ? state.error : null;
    return {
        state,
        upload,
        reset,
        isUploading,
        isSuccess,
        isError,
        result,
        error,
    };
}
//# sourceMappingURL=useFileUpload.js.map